<template>
  <div>
    <div :class="containerClass" class="d-flex justify-content-between align-items-center mt-3">
      <div class="d-flex align-items-center">
        <div class="back-result-lg font-weight-bold mr-2">
          <a href="#" @click.prevent="goBackToEnrollments" style="text-decoration: underline">
            <span class="material-icons icon-32pt">navigate_before</span>
          </a>
        </div>
        <h2 class="mb-0">Admin Course Analytics</h2>
      </div>
      <div class="d-flex align-items-center gap-8">
        <b-btn variant="outline-primary" @click="resetAll">Reset Filters</b-btn>
        <b-btn variant="primary" :disabled="isLoading.overview" @click="fetchOverview">Apply Filters</b-btn>
      </div>
    </div>

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-4">
          <div class="card-body">
            <div class="row">
              <div class="col-md-3 mb-2">
                <label class="form-label">School</label>
                <v-select
                  class="form-control v-select-custom"
                  label="name"
                  :reduce="option => option.id"
                  :options="schoolOptions"
                  v-model="filters.school_id"
                  placeholder="All Schools"
                  :clearable="true"
                  @input="onSchoolFilterChange"
                />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label">Course</label>
                <v-select
                  class="form-control v-select-custom"
                  label="title"
                  :reduce="option => option.id"
                  :options="courseOptions"
                  v-model="filters.course_id"
                  placeholder="All Courses"
                  :clearable="true"
                />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label">Status</label>
                <v-select
                  class="form-control v-select-custom"
                  label="label"
                  :reduce="option => option.value"
                  :options="statusOptions"
                  v-model="filters.status"
                  placeholder="All Statuses"
                  :clearable="true"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="card mb-4">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Schools Overview</h5>
            <span class="text-muted" v-if="selectedSchoolName">Selected: {{ selectedSchoolName }}</span>
          </div>
          <div class="card-body">
            <b-skeleton-wrapper :loading="isLoading.overview">
              <template #loading>
                <b-skeleton-table
                  :rows="5"
                  :columns="overviewFields.length"
                  :table-props="{ striped: true, hover: true, responsive: true }"
                ></b-skeleton-table>
              </template>

              <div v-if="overviewRows.length" class="table-responsive">
                <b-table
                  id="schools-overview-table"
                  :items="overviewRows"
                  :fields="overviewFields"
                  striped
                  hover
                  responsive
                  :tbody-tr-class="overviewRowClass"
                  @row-clicked="openSchoolCourses"
                >
                  <template #cell(school_name)="row">
                    <div class="d-flex align-items-center">
                      <b-avatar
                        :src="resolveSchoolLogo(row.item)"
                        :text="schoolInitials(row.item.school_name)"
                        variant="light"
                        size="30px"
                        class="mr-2 school-logo-avatar"
                      />
                      <span>{{ row.item.school_name || '-' }}</span>
                    </div>
                  </template>
                  <template #cell(avg_progress_percent)="row">{{ formatPercent(row.item.avg_progress_percent) }}</template>
                  <template #cell(avg_grade)="row">{{ formatPercent(row.item.avg_grade) }}</template>
                  <template #cell(pass_rate)="row">{{ formatPercent(row.item.pass_rate) }}</template>
                  <template #cell(actions)="row">
                    <b-btn size="sm" variant="outline-primary" @click.stop="openSchoolCourses(row.item)">View Details</b-btn>
                  </template>
                </b-table>
              </div>
              <div v-else class="text-center text-muted py-4">No schools data found.</div>
            </b-skeleton-wrapper>
          </div>
          <div class="card-footer" v-if="overviewTotalRows > overviewPerPage">
            <pagination
              v-model="overviewCurrentPage"
              :total-rows="overviewTotalRows"
              :per-page="overviewPerPage"
              @change="onOverviewPageChange"
              aria-controls="schools-overview-table"
            />
          </div>
        </div>
      </div>
    </div>

    <b-modal
      id="sa-school-courses-modal"
      title="School Details"
      size="xl"
      centered
      scrollable
      hide-footer
      @hidden="clearSchoolSelection"
    >
      <div class="mb-3"><strong>School:</strong> {{ selectedSchoolName || '-' }}</div>
      <div class="mb-3 text-muted" v-if="appliedFilterSummary">Applied Filters: {{ appliedFilterSummary }}</div>
      <b-skeleton-wrapper :loading="isLoading.courses">
        <template #loading>
          <b-skeleton-table
            :rows="5"
            :columns="courseFields.length"
            :table-props="{ striped: true, hover: true, responsive: true }"
          ></b-skeleton-table>
        </template>

        <div v-if="courseRows.length" class="table-responsive">
          <b-table
            :items="courseRows"
            :fields="courseFields"
            striped
            hover
            responsive
          >
            <template #cell(avg_progress_percent)="row">{{ formatPercent(row.item.avg_progress_percent) }}</template>
            <template #cell(avg_grade)="row">{{ formatPercent(row.item.avg_grade) }}</template>
            <template #cell(pass_rate)="row">{{ formatPercent(row.item.pass_rate) }}</template>
          </b-table>
        </div>
        <div v-else class="text-center text-muted py-4">No courses for selected school.</div>
      </b-skeleton-wrapper>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import Pagination from '@/components/Ui/Pagination.vue';

export default {
  components: {
    Pagination,
  },
  extends: Page,

  data() {
    return {
      filters: {
        school_id: null,
        course_id: null,
        status: null,
      },
      schoolOptions: [],
      courseOptions: [],
      statusOptions: [
        { label: 'Enrolled', value: 'enrolled' },
        { label: 'In Progress', value: 'in_progress' },
        { label: 'Completed', value: 'completed' },
        { label: 'Dropped', value: 'dropped' },
      ],
      selectedSchoolId: null,
      selectedSchoolName: '',
      overviewRows: [],
      overviewPerPage: 10,
      overviewCurrentPage: 1,
      overviewTotalRows: 0,
      courseRows: [],
      isLoading: {
        bootstrap: false,
        overview: false,
        courses: false,
      },
      courseFields: [
        { key: 'course_title', label: 'Course' },
        { key: 'program_category_name', label: 'Program' },
        { key: 'enrollment_count', label: 'Enrollments' },
        { key: 'avg_progress_percent', label: 'Avg Progress' },
        { key: 'avg_grade', label: 'Avg Grade' },
        { key: 'pass_rate', label: 'Pass Rate' },
      ],
    };
  },

  methods: {
    ...mapActions('school', ['getAllSchools']),
    ...mapActions('courses', [
      'getLmsCourses',
      'getSuperAnalyticsSchoolsOverview',
      'getSuperAnalyticsSchoolCourses',
    ]),

    extractListPayload(data) {
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.results)) return data.results;
      return [];
    },

    unwrapApiData(response) {
      if (response && Object.prototype.hasOwnProperty.call(response, 'data')) {
        return response.data;
      }
      return response;
    },

    buildQuery(params) {
      const query = {};
      Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        const strValue = String(value).trim();
        if (!strValue) return;
        query[key] = value;
      });
      return query;
    },

    toErrorMessage(error, fallback) {
      return error?.response?.data?.message || error?.response?.data?.error || fallback;
    },

    async bootstrapFilters() {
      this.isLoading.bootstrap = true;
      try {
        const [schoolsRes, coursesRes] = await Promise.all([
          this.getAllSchools({ pagination: false }),
          this.getLmsCourses({ pagination: false }),
        ]);

        this.schoolOptions = this.extractListPayload(this.unwrapApiData(schoolsRes)).map(item => ({
          id: item.id,
          name: item.name,
          logo_url: item.logo_url || item.logo || item.image || item.image_url || '',
        }));

        this.courseOptions = this.extractListPayload(this.unwrapApiData(coursesRes)).map(item => ({
          id: item.id,
          title: item.title,
        }));
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.toErrorMessage(error, this.$t('generalMsgs.genErrorMsg')) });
      }
      this.isLoading.bootstrap = false;
    },

    async fetchOverview(pageNum = 1) {
      const numericPage = Number(pageNum);
      const safePage = Number.isFinite(numericPage) && numericPage > 0 ? numericPage : 1;
      this.isLoading.overview = true;
      this.clearSchoolSelection();
      try {
        const response = await this.getSuperAnalyticsSchoolsOverview(
          this.buildQuery({
            ...this.filters,
            limit: this.overviewPerPage,
            offset: (safePage - 1) * this.overviewPerPage,
          }),
        );
        const overviewData = response?.data;
        this.overviewRows = this.extractListPayload(overviewData).map(row => ({
          ...row,
          school_logo_url:
            row.school_logo_url || row.school_logo || row.logo_url || row.logo || this.findSchoolLogoById(row.school_id),
        }));
        this.overviewCurrentPage = safePage;
        this.overviewTotalRows = Number(overviewData?.count || this.overviewRows.length || 0);
      } catch (error) {
        this.overviewRows = [];
        this.overviewTotalRows = 0;
        this.makeToast({ variant: 'danger', msg: this.toErrorMessage(error, this.$t('generalMsgs.genErrorMsg')) });
      }
      this.isLoading.overview = false;
    },

    onOverviewPageChange(pageNum) {
      this.fetchOverview(pageNum);
    },

    async openSchoolCourses(row) {
      this.selectedSchoolId = row.school_id;
      this.selectedSchoolName = row.school_name;
      this.courseRows = [];
      this.$bvModal.show('sa-school-courses-modal');
      this.isLoading.courses = true;
      try {
        const response = await this.getSuperAnalyticsSchoolCourses(
          this.buildQuery({
            school_id: this.selectedSchoolId,
            course_id: this.filters.course_id,
            status: this.filters.status,
          }),
        );
        this.courseRows = this.extractListPayload(response?.data);
      } catch (error) {
        this.courseRows = [];
        this.makeToast({ variant: 'danger', msg: this.toErrorMessage(error, this.$t('generalMsgs.genErrorMsg')) });
      }
      this.isLoading.courses = false;
    },

    clearSchoolSelection() {
      this.selectedSchoolId = null;
      this.selectedSchoolName = '';
      this.courseRows = [];
    },

    onSchoolFilterChange() {
      this.clearSchoolSelection();
    },

    resetAll() {
      this.filters = {
        school_id: null,
        course_id: null,
        status: null,
      };
      this.overviewRows = [];
      this.overviewCurrentPage = 1;
      this.overviewTotalRows = 0;
      this.clearSchoolSelection();
      this.fetchOverview(1);
    },

    goBackToEnrollments() {
      this.$router.push({ name: 'sa-course-enrollments' });
    },

    overviewRowClass() {
      return 'overview-clickable-row';
    },

    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '0.00';
      const num = Number(value);
      if (Number.isNaN(num)) return '0.00';
      return num.toFixed(2);
    },

    formatPercent(value) {
      return `${this.formatNumber(value)}%`;
    },

    findSchoolLogoById(schoolId) {
      const school = this.schoolOptions.find(item => item.id === schoolId);
      return (school && school.logo_url) || '';
    },

    resolveSchoolLogo(item) {
      return (
        item.school_logo_url || item.school_logo || item.logo_url || item.logo || this.findSchoolLogoById(item.school_id) || ''
      );
    },

    schoolInitials(name) {
      if (!name) return 'S';
      const parts = String(name)
        .trim()
        .split(' ')
        .filter(Boolean);
      const first = parts[0] ? parts[0][0] : '';
      const second = parts[1] ? parts[1][0] : '';
      return `${first}${second}`.toUpperCase() || 'S';
    },
  },

  computed: {
    overviewFields() {
      const fields = [
        { key: 'school_name', label: 'School' },
        { key: 'total_courses', label: 'Courses' },
        { key: 'total_enrollments', label: 'Enrollments' },
        { key: 'avg_progress_percent', label: 'Avg Progress' },
        { key: 'avg_grade', label: 'Avg Grade' },
        { key: 'pass_rate', label: 'Pass Rate' },
        { key: 'actions', label: 'Actions' },
      ];

      if (this.filters.course_id) {
        return fields.filter(field => field.key !== 'total_courses');
      }
      return fields;
    },

    appliedFilterSummary() {
      const labels = [];
      if (this.filters.course_id) {
        const selected = this.courseOptions.find(option => option.id === this.filters.course_id);
        labels.push(`Course: ${(selected && selected.title) || this.filters.course_id}`);
      }
      if (this.filters.status) {
        const selected = this.statusOptions.find(option => option.value === this.filters.status);
        labels.push(`Status: ${(selected && selected.label) || this.filters.status}`);
      }
      return labels.join(' | ');
    },
  },

  async mounted() {
    this.filters.course_id = (this.$route && this.$route.query && this.$route.query.course_id) || null;
    this.filters.status = (this.$route && this.$route.query && this.$route.query.status) || null;
    await Promise.all([this.fetchOverview(), this.bootstrapFilters()]);
  },
};
</script>

<style scoped>
.gap-8 {
  gap: 8px;
}

::v-deep .overview-clickable-row td {
  cursor: pointer;
}

::v-deep .overview-clickable-row:hover td {
  background: #f8fbff;
}

::v-deep .school-logo-avatar {
  background-color: #fff !important;
  border: 1px solid #e6edf5;
}
</style>
